// -----------------------------------------------------------------------------
// FollowUpPanel  (Task #26 → multi-channel via the 4-card picker)
//
// PURPOSE — LOGS *PAST* CONTACT EVENTS across the four follow-up channels:
//
//     Call · WhatsApp · Email · Note   (no forced order)
//
// UX: a 4-card picker. Tap a card → do that channel via its EXISTING sub-panel
// (real action) → on complete, RETURN to the picker with that channel marked
// done ✓. Tap more cards or tap Finish to complete the item. The user is free to
// do one channel or several, in any order — no "do you want to do X next?".
//
// Each channel performs its REAL action:
//   • Call  → LogCallPanel  (POST note, contacted_via=PHONE)
//   • Note  → AddNotePanel  (POST note)
//   • Email → onOpenSidebarTab('email')    + SidebarRedirectConfirmation (POST note,
//             contacted_via=EMAIL)
//   • WA    → onOpenSidebarTab('whatsapp') + SidebarRedirectConfirmation (POST note,
//             contacted_via=WHATSAPP)
// Those actions already emit their own lead-timeline events, so DONE channels land
// in the timeline for free; untouched channels post nothing.
//
// The checklist ITEM completes ONCE — when the user taps Finish (or after the 4th
// channel is done). We pass the per-channel outcomes up so the orchestrator
// persists them in response_payload = { resolved_action:'follow_up',
// channels:[{channel,state:'done'}…] } (no BE migration). Only DONE channels are
// recorded — we do not force a 'skipped' state on untouched channels.
//
// Shown when item_type === 'LEAD_NEXT_ACTION' or 'OPEN_FOLLOW_UP'. Other item
// types and the single-channel panels are UNCHANGED.
//
// Props mirror the standard panel contract:
//   item, practiceId, leadId, leadDetail, onClose, onComplete, handleReloadLeads,
//   onOpenSidebarTab, noSidebar
// -----------------------------------------------------------------------------
import PropTypes from 'prop-types';
import { useState, useCallback, useRef, useEffect } from 'react';
import { Box, Typography, Stack, Button } from '@mui/material';
import Iconify from 'src/components/Iconify';
import { callApi } from 'src/utils/apiCall';
import { parseWtmResolveResponse } from 'src/components/whatsappComponents/quickReplyUtils';
import LogCallPanel from './LogCallPanel';
import AddNotePanel from './AddNotePanel';
import SidebarRedirectConfirmation from './SidebarRedirectConfirmation';

// ---------------------------------------------------------------------------
// Debug overlay toggle — mirrors AttendedAppointment: ?attDebug=1 OR
// localStorage attDebug='1' OR DEV (and not explicitly '0').
const getAttDebug = () =>
    new URLSearchParams(window.location.search).get('attDebug') === '1'
    || (typeof window !== 'undefined' && window.localStorage.getItem('attDebug') === '1')
    || (import.meta.env.DEV
        && new URLSearchParams(window.location.search).get('attDebug') !== '0'
        && !(typeof window !== 'undefined' && window.localStorage.getItem('attDebug') === '0'));

const DBG_TAG_SX = (color) => ({
    position: 'absolute',
    top: 0,
    left: 0,
    zIndex: 50,
    bgcolor: color,
    color: '#fff',
    fontSize: '9px',
    fontWeight: 700,
    lineHeight: 1.4,
    px: 0.5,
    borderBottomRightRadius: 4,
    letterSpacing: '0.04em',
    pointerEvents: 'none',
    textTransform: 'uppercase',
});

// ---------------------------------------------------------------------------
// Channel definitions
// ---------------------------------------------------------------------------
const CHANNELS = [
    {
        key: 'call',
        label: 'Call',
        icon: 'solar:phone-bold',
        color: '#00A76F',
        bg: 'rgba(0,167,111,0.08)',
        border: 'rgba(0,167,111,0.20)',
    },
    {
        key: 'whatsapp',
        label: 'WhatsApp',
        icon: 'ic:baseline-whatsapp',
        color: '#25D366',
        bg: 'rgba(37,211,102,0.08)',
        border: 'rgba(37,211,102,0.20)',
    },
    {
        key: 'email',
        label: 'Email',
        icon: 'mdi:email-outline',
        color: '#078DEE',
        bg: 'rgba(7,141,238,0.08)',
        border: 'rgba(7,141,238,0.20)',
    },
    {
        key: 'note',
        label: 'Note',
        icon: 'mdi:note-text-outline',
        color: '#FFAB00',
        bg: 'rgba(255,171,0,0.08)',
        border: 'rgba(255,171,0,0.20)',
    },
];

// ---------------------------------------------------------------------------
// Shared containment sx — applied to every sub-panel wrapper so no child
// component can overflow the ActionPanel card's allocated slot.
// ---------------------------------------------------------------------------
const SUB_PANEL_SX = {
    width: '100%',
    minWidth: 0,
    overflowX: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
};

// ---------------------------------------------------------------------------
// ChannelPicker — the 4-card picker. Cards labelled Call · WhatsApp · Email ·
// Note. A channel already done shows a green ✓ instead of the chevron and a
// subtle done treatment. A "Finish" button completes the item once.
// ---------------------------------------------------------------------------
function ChannelPicker({ onSelect, doneChannels, onFinish, onCancel }) {
    const attDebug = getAttDebug();
    const doneCount = doneChannels.length;
    return (
        <Box
            data-testid="follow-up-channel-picker"
            sx={{ ...SUB_PANEL_SX, px: 2.5, py: 2, gap: 1.5, width: '100%' }}
        >
            <Box>
                <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#1d1d1f', letterSpacing: '-0.02em' }}>
                    How did you follow up?
                </Typography>
                <Typography sx={{ fontSize: '13px', color: '#6e6e73', mt: 0.5, letterSpacing: '-0.005em' }}>
                    {doneCount > 0
                        ? 'Log another channel, or tap Finish when you are done.'
                        : 'Choose a channel to contact this patient. You can log more than one.'}
                </Typography>
            </Box>

            <Stack spacing={1} sx={attDebug ? { position: 'relative' } : undefined}>
                {attDebug && <Box component="span" sx={DBG_TAG_SX('#FF6D00')}>Follow-up channels</Box>}
                {CHANNELS.map((ch) => {
                    const isDone = doneChannels.includes(ch.key);
                    return (
                        <Box
                            key={ch.key}
                            component="button"
                            type="button"
                            data-testid={`follow-up-channel-${ch.key}`}
                            data-done={isDone ? 'true' : 'false'}
                            onClick={() => onSelect(ch.key)}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                px: 2,
                                py: 1.25,
                                borderRadius: '12px',
                                border: `1.5px solid ${isDone ? 'rgba(0,167,111,0.40)' : ch.border}`,
                                bgcolor: isDone ? 'rgba(0,167,111,0.06)' : ch.bg,
                                cursor: 'pointer',
                                textAlign: 'left',
                                transition: 'all 0.15s ease',
                                '&:hover': {
                                    filter: 'brightness(0.96)',
                                    transform: 'translateY(-1px)',
                                    boxShadow: `0 3px 10px ${ch.bg}`,
                                },
                                '&:active': { transform: 'none' },
                                ...(attDebug && { outline: '2px dashed #FF6D00', outlineOffset: '2px' }),
                            }}
                        >
                            <Box
                                sx={{
                                    width: 38,
                                    height: 38,
                                    borderRadius: '10px',
                                    bgcolor: ch.bg,
                                    border: `1px solid ${ch.border}`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                }}
                            >
                                <Iconify icon={ch.icon} width={20} height={20} sx={{ color: ch.color }} />
                            </Box>
                            <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#1d1d1f', letterSpacing: '-0.01em' }}>
                                {ch.label}
                            </Typography>
                            {isDone ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
                                    <Iconify icon="eva:checkmark-circle-2-fill" width={18} height={18} sx={{ color: '#00A76F' }} />
                                    <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#00A76F', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                                        Done
                                    </Typography>
                                </Box>
                            ) : (
                                <Iconify icon="eva:chevron-right-fill" width={16} height={16} sx={{ color: '#86868b', ml: 'auto' }} />
                            )}
                        </Box>
                    );
                })}
            </Stack>

            {/* Finish — completes the checklist item once. Primary green when at
                least one channel is done; quiet text when nothing was logged yet
                (lets the user complete with no contact recorded if they choose). */}
            <Button
                data-testid="follow-up-finish-btn"
                fullWidth
                variant={doneCount > 0 ? 'contained' : 'text'}
                onClick={onFinish}
                startIcon={doneCount > 0 ? <Iconify icon="eva:checkmark-fill" width={16} height={16} /> : null}
                sx={doneCount > 0
                    ? {
                        mt: 0.5,
                        bgcolor: '#00A76F',
                        color: '#fff',
                        textTransform: 'none',
                        borderRadius: '10px',
                        fontSize: '14px',
                        fontWeight: 600,
                        boxShadow: 'none',
                        '&:hover': { bgcolor: '#007867', boxShadow: 'none' },
                    }
                    : {
                        mt: 0.5,
                        color: '#6e6e73',
                        textTransform: 'none',
                        fontSize: '13px',
                        fontWeight: 500,
                        '&:hover': { bgcolor: 'rgba(145,158,171,0.10)' },
                    }}
            >
                {doneCount > 0 ? `Finish${doneCount > 1 ? ` (${doneCount} logged)` : ''}` : 'Finish without logging'}
            </Button>

            {/* Cancel — dismiss the panel WITHOUT completing the checklist item
                (distinct from Finish, which marks the item done). */}
            {onCancel && (
                <Button
                    data-testid="follow-up-cancel-btn"
                    fullWidth
                    variant="text"
                    onClick={onCancel}
                    sx={{
                        color: '#919EAB',
                        textTransform: 'none',
                        fontSize: '12px',
                        fontWeight: 500,
                        mt: -0.5,
                        '&:hover': { bgcolor: 'rgba(145,158,171,0.08)' },
                    }}
                >
                    Cancel
                </Button>
            )}
        </Box>
    );
}

ChannelPicker.propTypes = {
    onSelect: PropTypes.func.isRequired,
    doneChannels: PropTypes.arrayOf(PropTypes.string).isRequired,
    onFinish: PropTypes.func.isRequired,
    onCancel: PropTypes.func,
};
ChannelPicker.defaultProps = { onCancel: null };

// ---------------------------------------------------------------------------
// FollowUpPanel — picker ↔ sub-panel, multi-channel.
// ---------------------------------------------------------------------------
export default function FollowUpPanel({
    item,
    practiceId,
    leadId,
    leadDetail,
    onClose,
    onComplete,
    handleReloadLeads,
    onOpenSidebarTab,
    noSidebar,
}) {
    // channel = the card currently being acted on (null = showing the picker).
    // item.initialChannel (optional) — when set (standalone task action), skip the
    // picker and open that channel's sub-panel directly on mount.
    // doneChannels = channels whose real action has completed this session.
    const [channel, setChannel] = useState(item?.initialChannel || null);
    // ETM: token guarding the async email-template resolve. Bumped on every
    // channel select + on unmount so a late resolve can't preload a now-wrong
    // template into the composer (matches ActionPanel's `active` flag pattern).
    const emailResolveToken = useRef(0);
    useEffect(() => () => { emailResolveToken.current += 1; }, []);
    const [doneChannels, setDoneChannels] = useState([]);

    const initialChannel = item?.initialChannel;

    // Resolve the per-practice ETM/WTM mapping for an email/whatsapp channel and
    // route it to the sidebar composer. Shared by handleChannelSelect (picker) AND
    // the initialChannel mount effect (standalone task opened directly in a
    // channel) so a pre-selected task preloads its mapped template/draft too —
    // previously the mount path called onOpenSidebarTab with NO opts, so the
    // resolve never ran and the composer opened blank even when mapped.
    // The caller bumps emailResolveToken and passes its captured myToken for the
    // staleness guard. Forwards the checklist item id so the mapped draft's
    // dismissed flag is scoped per item.
    const resolveMappingForChannel = useCallback((ch, myToken) => {
        if (ch !== 'email' && ch !== 'whatsapp') return;
        const resolvePracticeId = leadDetail?.practice || leadDetail?.practice_id || practiceId;
        const templateItemId = item?.template_item;
        if (!templateItemId || !resolvePracticeId || !onOpenSidebarTab) return;
        const rowItemId = item?.id ?? templateItemId;
        if (ch === 'email') {
            callApi(`/api/v1/practices/${resolvePracticeId}/checklist-email-mappings/resolve/?template_checklist_item=${templateItemId}`)
                .then((res) => {
                    if (emailResolveToken.current !== myToken) return;
                    const id = res?.data?.[0]?.engagement_template_id ?? null;
                    if (id != null) onOpenSidebarTab('email', { templateId: id, itemId: rowItemId });
                })
                .catch(() => { /* silent — composer stays blank */ });
        } else {
            callApi(`/api/v1/practices/${resolvePracticeId}/checklist-whatsapp-mappings/resolve/?template_checklist_item=${templateItemId}`)
                .then((res) => {
                    if (emailResolveToken.current !== myToken) return;
                    const wtm = parseWtmResolveResponse(res);
                    if (wtm) onOpenSidebarTab('whatsapp', { templateName: wtm.name, templateWaId: wtm.id, itemId: rowItemId });
                })
                .catch(() => { /* silent — composer stays blank */ });
        }
    }, [onOpenSidebarTab, item, leadDetail, practiceId]);

    // Standalone task pre-selected a channel → open the sidebar tab AND resolve its
    // mapping on mount (mirrors handleChannelSelect) so it doesn't open blank.
    useEffect(() => {
        if (initialChannel && (initialChannel === 'email' || initialChannel === 'whatsapp') && onOpenSidebarTab) {
            emailResolveToken.current += 1;
            const myToken = emailResolveToken.current;
            onOpenSidebarTab(initialChannel);
            resolveMappingForChannel(initialChannel, myToken);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Finalize the whole item ONCE. The orchestrator's handlePanelComplete runs
    // completeChecklistItem with the merged payload { resolved_action:'follow_up',
    // channels:[{channel,state:'done'}…] }. Only done channels are recorded.
    const finalize = useCallback((doneList) => {
        if (!onComplete) return;
        onComplete({
            ...item,
            resolvedAction: 'follow_up',
            channelOutcomes: doneList.map((c) => ({ channel: c, state: 'done' })),
        });
    }, [onComplete, item]);

    const handleFinish = useCallback(() => finalize(doneChannels), [finalize, doneChannels]);

    // For Email/WA: open the matching sidebar tab (if available), then show the
    // confirmation view inside the action panel.
    const handleChannelSelect = useCallback((ch) => {
        // Invalidate any in-flight resolve from a prior selection, then open the
        // tab + resolve the mapping (ETM email / WTM whatsapp) via the shared
        // helper — same path the initialChannel mount effect uses.
        emailResolveToken.current += 1;
        const myToken = emailResolveToken.current;
        if ((ch === 'email' || ch === 'whatsapp') && onOpenSidebarTab) {
            onOpenSidebarTab(ch);
            resolveMappingForChannel(ch, myToken);
        }
        setChannel(ch);
    }, [onOpenSidebarTab, resolveMappingForChannel]);

    const handleBack = useCallback(() => setChannel(null), []);
    // When a channel was pre-selected (standalone task), there's no picker to return to.
    // "Back" should close the whole panel instead of showing a picker the user never saw.
    const handleChannelBack = initialChannel ? onClose : handleBack;

    // A channel's sub-panel finished its REAL action → mark done + either RETURN
    // to the picker (multi-channel flow) or FINALIZE immediately (standalone task).
    //
    // Standalone path (initialChannel set): the user opened a single pre-selected
    // channel — there's no picker to return to and no follow-up prompt should show.
    // After the action is done, complete the task straight away.
    // Normal path (initialChannel null): go back to picker so the user can log
    // additional channels before tapping Finish.
    const handleChannelDone = useCallback(() => {
        const updatedDone = channel && !doneChannels.includes(channel)
            ? [...doneChannels, channel]
            : doneChannels;
        setDoneChannels(updatedDone);
        if (initialChannel) {
            // Standalone single-action task: skip the picker, finalize immediately.
            finalize(updatedDone);
        } else {
            setChannel(null);
        }
    }, [channel, doneChannels, initialChannel, finalize]);

    // ── Step 1: the 4-card picker ──────────────────────────────────────────────
    if (!channel) {
        return (
            <ChannelPicker
                onSelect={handleChannelSelect}
                doneChannels={doneChannels}
                onFinish={handleFinish}
                onCancel={onClose}
            />
        );
    }

    // ── Step 2: the selected channel's sub-panel ────────────────────────────────
    // onComplete = handleChannelDone (sub-panel already performed the real action).
    // onClose returns to the picker (does NOT abandon the whole panel).
    const subPanelProps = {
        item,
        practiceId,
        leadId,
        leadDetail,
        onClose: handleBack,
        onComplete: handleChannelDone,
        handleReloadLeads,
        onOpenSidebarTab,
    };

    if (channel === 'email' || channel === 'whatsapp') {
        return (
            <SidebarRedirectConfirmation
                channel={channel}
                body="Composer opened in the sidebar — send your message there, then mark this channel done."
                doneLabel={`Mark ${channel === 'email' ? 'email' : 'WhatsApp'} done`}
                onBack={handleChannelBack}
                onComplete={handleChannelDone}
                practiceId={practiceId}
                leadId={leadId}
                leadDetail={leadDetail}
                noSidebar={noSidebar}
            />
        );
    }

    if (channel === 'call') {
        // Back-to-channels moves into LogCallPanel's footer (left of "Log call"),
        // matching where the other panels place their back control.
        return (
            <Box sx={SUB_PANEL_SX}>
                <LogCallPanel
                    {...subPanelProps}
                    onBack={handleChannelBack}
                    backLabel={initialChannel ? 'Close' : 'Back'}
                />
            </Box>
        );
    }

    if (channel === 'note') {
        // Back moves into AddNotePanel's footer (left of "Save note"), matching the call panel.
        return (
            <Box sx={SUB_PANEL_SX}>
                <AddNotePanel
                    {...subPanelProps}
                    onBack={handleChannelBack}
                    backLabel={initialChannel ? 'Close' : 'Back'}
                />
            </Box>
        );
    }

    return null;
}

FollowUpPanel.propTypes = {
    item: PropTypes.object,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    leadDetail: PropTypes.object,
    onClose: PropTypes.func.isRequired,
    onComplete: PropTypes.func,
    handleReloadLeads: PropTypes.func,
    /** Called with 'email' or 'whatsapp' to open the matching right-sidebar tab. */
    onOpenSidebarTab: PropTypes.func,
    /** Additive, default false. When true, Email/WA confirmation copy is sidebar-free. */
    noSidebar: PropTypes.bool,
};

FollowUpPanel.defaultProps = {
    item: null,
    practiceId: null,
    leadId: null,
    leadDetail: null,
    onComplete: null,
    handleReloadLeads: null,
    onOpenSidebarTab: null,
    noSidebar: false,
};
